import React, { useState, useRef } from 'react'; // Added useRef
import { useTranslation } from 'react-i18next';
import { Phone, MessageCircle, Mic, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import AudioLines from '../../components/ui/AudioLines';

// Import the audio file
import callSound from '../assets/sounds/showcase.mp3'; // Adjust the path and filename as needed

const HowTo = () => {
  const { t } = useTranslation();
  const [isCallOpen, setIsCallOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(new Audio(callSound));

  // Optional: Preload the audio using useRef
  // import { useRef } from 'react';
  // const audioRef = useRef(new Audio(callSound));

  const steps = [
    {
      number: 1,
      title: 'Create an AI agent',
      description: 'Describe what you want your agent to do.',
    },
    {
      number: 2,
      title: 'Select your number',
      description: 'Choose a local number for your AI to use.',
    },
    {
      number: 3,
      title: 'Let AI answer for you',
      description: 'Your agent will answer your calls and texts.',
    },
  ];

  const handleCall = () => {
    setIsCallOpen(true);
    
    // Play the audio using the ref
    audioRef.current.play().catch((error) => {
    console.error("Error playing audio:", error);
  });

    // Option 2: Use a preloaded audio reference (if using useRef)
    // if (audioRef.current) {
    //   audioRef.current.currentTime = 0;
    //   audioRef.current.play().catch((error) => {
    //     console.error("Error playing audio:", error);
    //   });
    // }
  };

  const handleChat = () => {
    setIsChatOpen(true);
  };

  return (
    <section className="py-12 bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
            {t('howto.heading')}
          </h2>
          <p className="text-lg text-gray-600 mt-4">
            {t('howto.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column - Steps */}
          <div className="space-y-6">
            {steps.map((step) => (
              <div
                key={step.number}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium">{step.number}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t(`howto.steps.${step.number}.title`)}</h3>
                    <p className="mt-1 text-gray-500">{t(`howto.steps.${step.number}.description`)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Column - Preview */}
          <div className="flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-sm p-6 max-w-sm w-full">
              <div className="text-center mb-6">
                <p className="text-sm text-gray-500 uppercase tracking-wide font-medium">{t('howto.action.title')}</p>
              </div>
              <div className="bg-gray-900 text-white p-6 rounded-xl mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-lg">{t('howto.action.business_name')}</h4>
                    <p className="text-gray-300 text-sm">{t('howto.action.message')}</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <AudioLines className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <button
                  onClick={handleCall}
                  className="w-full py-2.5 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors duration-300"
                >
                  <Phone className="w-4 h-4" />
                  <span>{t('howto.action.call_button')}</span>
                </button>

              </div>
            </div>
          </div>
        </div>

        {/* Call Dialog */}
        <Dialog
            open={isCallOpen}
            onOpenChange={(open) => {
              setIsCallOpen(open);
              if (!open) {
                // Pause and reset the audio when the dialog is closed
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
              }
            }}
          >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center">
                <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <AudioLines className="w-6 h-6 text-white" />
                  </div>
                                    <h3>{t('howto.action.business_name')}</h3>
                  <p className="text-gray-500">{t('examples.actions.call_connecting')}</p>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center space-y-6">
              <button
                onClick={() => setIsMuted(!isMuted)}
                className={`p-4 rounded-full ${isMuted ? 'bg-gray-200' : 'bg-gray-100'} transition-colors duration-300`}
              >
                <Mic className="w-6 h-6" />
              </button>
              <button
                  onClick={() => {
                    setIsCallOpen(false);                    
                    // Pause and reset the audio
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                  }}
                className="w-full bg-red-500 text-white py-3 rounded-lg hover:bg-red-600 transition-colors duration-300"
              >
                {t('examples.actions.call_end')}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Chat Dialog */}
        <Dialog open={isChatOpen} onOpenChange={setIsChatOpen}>
          <DialogContent className="sm:max-w-md h-[600px]">
            <DialogHeader>
              <DialogTitle>Acme Solar</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-y-auto p-4">
                <div className="bg-gray-100 rounded-lg p-3 mb-4 max-w-[80%]">
                  <p>{t('examples.actions.chat_welcome', { business: 'Acme Solar' })}</p>
                </div>
              </div>
              <div className="border-t p-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    placeholder={t('examples.actions.chat_placeholder')}
                    className="flex-1 border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-300">
                    <MessageCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default HowTo;
